/**
 * Maps payment error messages and codes to user-friendly messages
 * Handles Stripe errors, validation errors, and API errors
 */

export const getPaymentErrorDetails = (error, response, t) => {
    // If error is a string, use it directly
    const errorMessage = typeof error === 'string' ? error : error?.message || '';
    // Check for code at multiple levels (root, data, data.data for nested responses)
    const errorCode = response?.code ||
                      response?.data?.code ||
                      response?.data?.declineCode ||
                      response?.data?.data?.code ||
                      response?.data?.data?.declineCode ||
                      '';
    const status = response?.status;

    // Map common Stripe decline codes to user-friendly messages
    const declineCodeMessages = {
        requires_action: {
            title: t?.('billing.errors.requiresAction.title') || 'Authentication Required',
            message: t?.('billing.errors.requiresAction.message') ||
                'Your payment requires additional authentication. You will be redirected to complete the verification.',
            action: t?.('billing.errors.requiresAction.action') || 'Complete verification',
        },
        authentication_required: {
            title: t?.('billing.errors.requiresAction.title') || 'Authentication Required',
            message: t?.('billing.errors.requiresAction.message') ||
                'Your payment requires additional authentication. You will be redirected to complete the verification.',
            action: t?.('billing.errors.requiresAction.action') || 'Complete verification',
        },
        requires_payment_method: {
            title: t?.('billing.errors.requiresPaymentMethod.title') || 'Payment Method Required',
            message: t?.('billing.errors.requiresPaymentMethod.message') ||
                'Your payment method needs to be updated. Please add or update your payment details.',
            action: t?.('billing.errors.requiresPaymentMethod.action') || 'Update payment method',
        },
        insufficient_funds: {
            title: t?.('billing.errors.insufficientFunds.title') || 'Insufficient Funds',
            message: t?.('billing.errors.insufficientFunds.message') ||
                'Your card has insufficient funds. Please try a different payment method or contact your bank.',
            action: t?.('billing.errors.insufficientFunds.action') || 'Try another card',
        },
        card_declined: {
            title: t?.('billing.errors.cardDeclined.title') || 'Card Declined',
            message: t?.('billing.errors.cardDeclined.message') ||
                'Your card was declined. Please contact your bank for more information or try a different payment method.',
            action: t?.('billing.errors.cardDeclined.action') || 'Use different card',
        },
        expired_card: {
            title: t?.('billing.errors.expiredCard.title') || 'Card Expired',
            message: t?.('billing.errors.expiredCard.message') ||
                'Your card has expired. Please use a different payment method.',
            action: t?.('billing.errors.expiredCard.action') || 'Update card',
        },
        incorrect_cvc: {
            title: t?.('billing.errors.incorrectCvc.title') || 'Incorrect CVC',
            message: t?.('billing.errors.incorrectCvc.message') ||
                'The security code (CVC) you entered is incorrect. Please check your card and try again.',
            action: t?.('billing.errors.incorrectCvc.action') || 'Check CVC',
        },
        incorrect_number: {
            title: t?.('billing.errors.incorrectNumber.title') || 'Incorrect Card Number',
            message: t?.('billing.errors.incorrectNumber.message') ||
                'The card number you entered is incorrect. Please check and try again.',
            action: t?.('billing.errors.incorrectNumber.action') || 'Check card number',
        },
        invalid_expiry_month: {
            title: t?.('billing.errors.invalidExpiry.title') || 'Invalid Expiry Date',
            message: t?.('billing.errors.invalidExpiry.message') ||
                'The expiration month is invalid. Please check your card details.',
            action: t?.('billing.errors.invalidExpiry.action') || 'Check expiry date',
        },
        invalid_expiry_year: {
            title: t?.('billing.errors.invalidExpiry.title') || 'Invalid Expiry Date',
            message: t?.('billing.errors.invalidExpiry.message') ||
                'The expiration year is invalid. Please check your card details.',
            action: t?.('billing.errors.invalidExpiry.action') || 'Check expiry date',
        },
        processing_error: {
            title: t?.('billing.errors.processingError.title') || 'Processing Error',
            message: t?.('billing.errors.processingError.message') ||
                'An error occurred while processing your card. Please try again or use a different payment method.',
            action: t?.('billing.errors.processingError.action') || 'Try again',
        },
        card_velocity_exceeded: {
            title: t?.('billing.errors.velocityExceeded.title') || 'Too Many Attempts',
            message: t?.('billing.errors.velocityExceeded.message') ||
                'You have exceeded the maximum number of payment attempts. Please wait a few minutes and try again.',
            action: t?.('billing.errors.velocityExceeded.action') || 'Wait and retry',
        },
        do_not_honor: {
            title: t?.('billing.errors.doNotHonor.title') || 'Card Declined',
            message: t?.('billing.errors.doNotHonor.message') ||
                'Your card was declined by your bank. Please contact your bank or try a different payment method.',
            action: t?.('billing.errors.doNotHonor.action') || 'Contact bank',
        },
        fraudulent: {
            title: t?.('billing.errors.fraudulent.title') || 'Payment Blocked',
            message: t?.('billing.errors.fraudulent.message') ||
                'This payment was blocked for security reasons. Please contact your bank or try a different payment method.',
            action: t?.('billing.errors.fraudulent.action') || 'Contact bank',
        },
        generic_decline: {
            title: t?.('billing.errors.genericDecline.title') || 'Card Declined',
            message: t?.('billing.errors.genericDecline.message') ||
                'Your card was declined. Please contact your bank or try a different payment method.',
            action: t?.('billing.errors.genericDecline.action') || 'Try another method',
        },
        lost_card: {
            title: t?.('billing.errors.lostCard.title') || 'Card Blocked',
            message: t?.('billing.errors.lostCard.message') ||
                'This card has been reported as lost. Please use a different payment method.',
            action: t?.('billing.errors.lostCard.action') || 'Use different card',
        },
        stolen_card: {
            title: t?.('billing.errors.stolenCard.title') || 'Card Blocked',
            message: t?.('billing.errors.stolenCard.message') ||
                'This card has been reported as stolen. Please use a different payment method.',
            action: t?.('billing.errors.stolenCard.action') || 'Use different card',
        },
        pickup_card: {
            title: t?.('billing.errors.pickupCard.title') || 'Card Blocked',
            message: t?.('billing.errors.pickupCard.message') ||
                'Your card cannot be used. Please contact your bank or use a different payment method.',
            action: t?.('billing.errors.pickupCard.action') || 'Contact bank',
        },
    };

    // Check for specific decline codes first
    if (errorCode && declineCodeMessages[errorCode]) {
        return declineCodeMessages[errorCode];
    }

    // Check error message for known patterns
    const lowerErrorMessage = errorMessage.toLowerCase();

    if (lowerErrorMessage.includes('insufficient funds')) {
        return declineCodeMessages.insufficient_funds;
    }
    if (lowerErrorMessage.includes('card was declined') || lowerErrorMessage.includes('card declined')) {
        return declineCodeMessages.card_declined;
    }
    if (lowerErrorMessage.includes('expired')) {
        return declineCodeMessages.expired_card;
    }
    if (lowerErrorMessage.includes('cvc') || lowerErrorMessage.includes('security code')) {
        return declineCodeMessages.incorrect_cvc;
    }
    if (lowerErrorMessage.includes('card number')) {
        return declineCodeMessages.incorrect_number;
    }
    if (lowerErrorMessage.includes('no payment method')) {
        return {
            title: t?.('billing.errors.noPaymentMethod.title') || 'No Payment Method',
            message: t?.('billing.errors.noPaymentMethod.message') ||
                'No payment method found. Please add a payment method to continue.',
            action: t?.('billing.errors.noPaymentMethod.action') || 'Add payment method',
        };
    }
    if (lowerErrorMessage.includes('payment method requires attention')) {
        return {
            title: t?.('billing.errors.paymentAttention.title') || 'Payment Method Issue',
            message: t?.('billing.errors.paymentAttention.message') ||
                'Your payment method requires attention. Please update your payment details and try again.',
            action: t?.('billing.errors.paymentAttention.action') || 'Update payment method',
        };
    }

    // Check for validation errors (status 400)
    if (status === 400) {
        return {
            title: t?.('billing.errors.validation.title') || 'Invalid Information',
            message: errorMessage || t?.('billing.errors.validation.message') ||
                'Please check your information and try again.',
            action: t?.('billing.errors.validation.action') || 'Check details',
        };
    }

    // Check for payment failed (status 402)
    if (status === 402) {
        return {
            title: t?.('billing.errors.paymentFailed.title') || 'Payment Failed',
            message: errorMessage || t?.('billing.errors.paymentFailed.message') ||
                'We couldn\'t process your payment. Please check your payment method and try again.',
            action: t?.('billing.errors.paymentFailed.action') || 'Try again',
        };
    }

    // Check for not found errors (status 404)
    if (status === 404) {
        return {
            title: t?.('billing.errors.notFound.title') || 'Not Found',
            message: errorMessage || t?.('billing.errors.notFound.message') ||
                'The requested resource was not found. Please refresh and try again.',
            action: t?.('billing.errors.notFound.action') || 'Refresh page',
        };
    }

    // Default error for 500 and other status codes
    return {
        title: t?.('billing.errors.default.title') || 'Payment Error',
        message: errorMessage || t?.('billing.errors.default.message') ||
            'An unexpected error occurred. Please try again or contact support if the problem persists.',
        action: t?.('billing.errors.default.action') || 'Try again',
    };
};

/**
 * Format error for display in CustomCheckoutForm
 */
export const formatPaymentError = (error, response, t) => {
    const details = getPaymentErrorDetails(error, response, t);
    return {
        title: details.title,
        message: details.message,
        action: details.action,
    };
};
